import { createContext, useContext, useState, useEffect } from "react";

const DEFAULT = {
  font_size: "medium",
  high_contrast: false,
  colour_blind_mode: "none",
  reduce_motion: false,
  read_aloud: false,
  show_hovercards: true,
  underline_links: false,
};

const AccessibilityContext = createContext({
  settings: DEFAULT,
  updateSettings: (s: any) => {},
  saveSettings: async (s: any) => {},
});

const FONT_SIZE_MAP: {[key: string]: string} = {
  small:  "12px",
  medium: "15px",
  large:  "18px",
  xlarge: "22px",
};

const CB_FILTERS: {[key: string]: string} = {
  none:         "none",
  deuteranopia: "url(#deuteranopia)",
  protanopia:   "url(#protanopia)",
  tritanopia:   "url(#tritanopia)",
};

function applySettings(settings: typeof DEFAULT) {
  const root = document.documentElement;
  const body = document.body;

  // Font size - apply to both html and body
  const fontSize = FONT_SIZE_MAP[settings.font_size] || "15px";
  root.style.fontSize = fontSize;
  body.style.fontSize = fontSize;

  // High contrast
  root.setAttribute("data-high-contrast", settings.high_contrast ? "true" : "false");

  // Colour blind filter
  body.style.filter = CB_FILTERS[settings.colour_blind_mode] || "none";

  // Reduce motion
  let motionStyle = document.getElementById("a11y-motion-style");
  if (settings.reduce_motion) {
    if (!motionStyle) {
      motionStyle = document.createElement("style");
      motionStyle.id = "a11y-motion-style";
      document.head.appendChild(motionStyle);
    }
    motionStyle.textContent = "*, *::before, *::after { animation: none !important; transition: none !important; }";
  } else {
    if (motionStyle) motionStyle.remove();
  }

  // Underline links
  let linkStyle = document.getElementById("a11y-link-style");
  if (settings.underline_links) {
    if (!linkStyle) {
      linkStyle = document.createElement("style");
      linkStyle.id = "a11y-link-style";
      document.head.appendChild(linkStyle);
    }
    linkStyle.textContent = "a { text-decoration: underline !important; }";
  } else {
    if (linkStyle) linkStyle.remove();
  }

  // Hovercards
  let hoverStyle = document.getElementById("a11y-hover-style");
  if (!settings.show_hovercards) {
    if (!hoverStyle) {
      hoverStyle = document.createElement("style");
      hoverStyle.id = "a11y-hover-style";
      document.head.appendChild(hoverStyle);
    }
    hoverStyle.textContent = "[data-hovercard] { display: none !important; }";
  } else {
    if (hoverStyle) hoverStyle.remove();
  }

  // Read aloud - set up selection listener
  const existingListener = (window as any)._readAloudListener;
  if (existingListener) {
    document.removeEventListener("mouseup", existingListener);
    (window as any)._readAloudListener = null;
    window.speechSynthesis && window.speechSynthesis.cancel();
  }

  if (settings.read_aloud) {
    const listener = () => {
      const selected = window.getSelection()?.toString().trim();
      if (selected && selected.length > 0) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(selected);
        utterance.lang = "en-IN";
        utterance.rate = 0.9;
        window.speechSynthesis.speak(utterance);
      }
    };
    document.addEventListener("mouseup", listener);
    (window as any)._readAloudListener = listener;
  }
}

function injectSVGFilters() {
  if (document.getElementById("cb-svg-filters")) return;
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.id = "cb-svg-filters";
  svg.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;";
  svg.innerHTML = `
    <defs>
      <filter id="deuteranopia">
        <feColorMatrix type="matrix" values="0.625 0.375 0 0 0  0.7 0.3 0 0 0  0 0.3 0.7 0 0  0 0 0 1 0"/>
      </filter>
      <filter id="protanopia">
        <feColorMatrix type="matrix" values="0.567 0.433 0 0 0  0.558 0.442 0 0 0  0 0.242 0.758 0 0  0 0 0 1 0"/>
      </filter>
      <filter id="tritanopia">
        <feColorMatrix type="matrix" values="0.95 0.05 0 0 0  0 0.433 0.567 0 0  0 0.475 0.525 0 0  0 0 0 1 0"/>
      </filter>
    </defs>`;
  document.body.appendChild(svg);
}

export function AccessibilityProvider({ children }: { children: any }) {
  const [settings, setSettings] = useState(DEFAULT);

  useEffect(() => {
    injectSVGFilters();
    const local = localStorage.getItem("accessibility");
    if (local) {
      try {
        const parsed = JSON.parse(local);
        const merged = { ...DEFAULT, ...parsed };
        setSettings(merged);
        applySettings(merged);
      } catch {}
    }
    const token = localStorage.getItem("token");
    if (token) {
      fetch("/api/accessibility/settings", {
        headers: { Authorization: "Bearer " + token }
      })
        .then(r => r.json())
        .then(data => {
          if (data && !data.detail) {
            const merged = { ...DEFAULT, ...data };
            setSettings(merged);
            applySettings(merged);
            localStorage.setItem("accessibility", JSON.stringify(merged));
          }
        })
        .catch(() => {});
    }
  }, []);

  const updateSettings = (newSettings: any) => {
    const merged = { ...settings, ...newSettings };
    setSettings(merged);
    applySettings(merged);
    localStorage.setItem("accessibility", JSON.stringify(merged));
  };

  const saveSettings = async (newSettings: any) => {
    const merged = { ...settings, ...newSettings };
    setSettings(merged);
    applySettings(merged);
    localStorage.setItem("accessibility", JSON.stringify(merged));
    const token = localStorage.getItem("token");
    if (token) {
      try {
        await fetch("/api/accessibility/settings", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token
          },
          body: JSON.stringify(merged)
        });
      } catch {}
    }
  };

  return (
    <AccessibilityContext.Provider value={{ settings, updateSettings, saveSettings }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export const useAccessibility = () => useContext(AccessibilityContext);
